import { useCallback } from "react";
import { LegendKey } from "../../../../models/chart_model";
import { COST_UNIT } from "../../../../Units";
import { getTimeRange } from "../../../../pages/Dashboard/Exchange3/Chart";
import { monthNames } from "../../../../pages/Dashboard/PriceForcating/IexMonthly";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { compareLegends } from "../utils/legendUtils";

interface Params {
  tooltipFilter?: (item: any, param: any) => boolean;
  secondXDataKey?: string;
  debouncedData: any[];
  isTimeSlot?: boolean;
  fontSize: number;
  isPriceForecast: boolean;
  legends: LegendKey[];
  hidePrice?: boolean;
  unit?: string;
  customLegendOrder?: string[];
  getDragTooltipState?: () => {
    active?: boolean;
    dataIndex?: number;
    seriesName?: string;
    accentColor?: string;
  } | null;
}

export function useTooltipFormatter({
  tooltipFilter,
  secondXDataKey,
  debouncedData,
  isTimeSlot,
  fontSize,
  isPriceForecast,
  legends,
  hidePrice,
  unit,
  customLegendOrder,
  getDragTooltipState,
}: Params) {
  return useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];
      if (tooltipFilter)
        items = items.filter((item) => tooltipFilter(item, param));

      const cleanName =
        typeof param.name === "string"
          ? normalizeDisplayLabel(param.name)
          : param.name;
      let title = cleanName;
      if (secondXDataKey && debouncedData[param.dataIndex]?.[secondXDataKey]) {
        title = isTimeSlot
          ? `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName} (${getTimeRange(parseInt(param.name))})`
          : `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName}`;
      } else if (isTimeSlot) {
        title = getTimeRange(parseInt(param.name)) + ` (${param.name})`;
      }

      const sortedItems = [...items].sort((a, b) =>
        compareLegends(
          a.seriesName || "",
          b.seriesName || "",
          customLegendOrder,
        ),
      );
      const validItems = sortedItems.filter((item) => {
        if (item.seriesName?.startsWith("__")) return false;
        if (item.value === null || item.value === undefined) return false;
        const value = parseFloat(item.value);
        return !isNaN(value) && isFinite(value);
      });

      const now = new Date();
      const currentMonth = monthNames[now.getMonth()];
      const currentYear = now.getFullYear().toString();
      const dragTooltipState = getDragTooltipState?.() || null;

      const tooltipItems = validItems.map((item) => {
        const year = item.seriesName?.trim();
        let displaySeriesName = item.seriesName;
        if (
          isPriceForecast &&
          item.name === currentMonth &&
          year === currentYear
        ) {
          displaySeriesName = `${currentYear} Forecast`;
        }

        const value = parseFloat(item.value)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const bgColor = item.color;
        const legend = legends.find((l) => l.name === item.seriesName);
        const isDragHighlight =
          !!dragTooltipState?.active &&
          dragTooltipState.dataIndex === param.dataIndex &&
          (item.seriesName === dragTooltipState.seriesName ||
            item.seriesId === dragTooltipState.seriesName);
        const dragAccent = dragTooltipState?.accentColor || "#E17A3A";

        let suffix = "";
        if (legend?.unit !== undefined) {
          suffix = legend.unit;
        } else {
          const suffixDict = {
            Price: COST_UNIT,
            Percent: "%",
            "%": "%",
            Cost: "Rs",
            Revenue: "Rs",
          };
          const foundKey = Object.keys(suffixDict).find((key) =>
            item.seriesName?.toString().includes(key),
          );
          suffix = foundKey
            ? suffixDict[foundKey as keyof typeof suffixDict]
            : legend?.unit || unit || "";
        }
        return `
    <div class="tooltip-item" data-name="${displaySeriesName?.toLowerCase()}">
      <div class="color-indicator" style="background-color: ${bgColor}"></div>
      <div class="series-name">${normalizeDisplayLabel(
        hidePrice ? displaySeriesName.replace("Price", "") : displaySeriesName,
      )}</div>
      <div class="value ${isDragHighlight ? "value--drag-active" : ""}" style="${isDragHighlight ? `color:${dragAccent};` : ""}">${value} ${suffix}</div>
    </div>
  `;
      });

      return `<div class="enhanced-tooltip"><div class="tooltip-header"><div class="tooltip-title">${title}</div></div><div class="tooltip-content">${tooltipItems.join("")}</div></div><style>.enhanced-tooltip{font-family:'Inter',system-ui,sans-serif;width:100%;min-width:200px}.tooltip-header{display:flex;flex-direction:column;gap:8px;border-bottom:1px solid #eaeaea;padding-bottom:8px;margin-bottom:8px}.tooltip-title{font-weight:600;font-size:${fontSize + 1}px;color:#333}.tooltip-content{max-height:300px;overflow-y:auto;padding-right:10px}.tooltip-item{display:flex;align-items:center;padding:4px 0;border-bottom:1px solid #f0f0f0}.tooltip-item:last-child{border-bottom:none}.color-indicator{width:10px;height:10px;border-radius:50%;margin-right:8px}.series-name{flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-size:${fontSize}px}.value{font-weight:500;margin-left:10px;font-size:${fontSize}px}.value--drag-active{font-weight:700;font-size:${fontSize + 2}px;line-height:1.1;text-shadow:0 0 0 transparent}</style>`;
    },
    [
      tooltipFilter,
      secondXDataKey,
      debouncedData,
      isTimeSlot,
      fontSize,
      isPriceForecast,
      legends,
      hidePrice,
      unit,
      customLegendOrder,
      getDragTooltipState,
    ],
  );
}
